import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormField from '../components/ui/FormField';
import ToggleSwitch from '../components/forms/ToggleSwitch';
import MultiSelect, { MultiSelectOption, SelectId } from '../components/MultiSelect';
import SearchableSelect from '../components/SearchableSelect';
import MarkdownEditor from '../components/MarkdownEditor';
import type { MetaField, MetaRelation, SchemaFieldProps } from './types';
import { getTargetFetcher, type TargetOption } from './targets';

/**
 * Loud development warning box rendered when a field cannot be resolved or a
 * json/array/custom field has no override. Never silently skips a field.
 */
export const DevWarningBox: React.FC<{ message: string }> = ({ message }) => (
  <div
    role="alert"
    style={{
      border: '2px dashed var(--danger, #dc3545)',
      background: 'rgba(220, 53, 69, 0.08)',
      color: 'var(--danger, #dc3545)',
      padding: '0.75rem 1rem',
      borderRadius: '6px',
      marginBottom: '1rem',
      fontSize: '0.85rem',
      fontFamily: 'monospace',
    }}
  >
    <strong>SchemaForm:</strong> {message}
  </div>
);

const baseInputStyle: React.CSSProperties = {};

/**
 * Default widget for a scalar field, keyed by kind. Reuses FormField for the
 * label/error scaffolding and the project's native input conventions.
 */
export const DefaultFieldWidget: React.FC<
  SchemaFieldProps & {
    entity: string;
    /** Per-value enum label keys (reuses existing heterogeneous i18n keys). */
    enumLabelKeys?: Record<string, string>;
    /** Option fetcher for `kind: 'fk'` fields; overrides the target registry. */
    optionsFetcher?: () => Promise<TargetOption[]>;
  }
> = ({ field, value, onChange, error, entity, enumLabelKeys, optionsFetcher }) => {
  const { t } = useTranslation();
  const label = t(field.labelKey, field.name);
  const id = `sf-${field.name}`;

  // Widget hint takes priority over the derived kind. `markdown` wires the
  // shared MarkdownEditor (used by the legacy Activity/Course description UI).
  if (field.widget === 'markdown') {
    return (
      <FormField htmlFor={id} label={label} required={field.required} error={error}>
        <MarkdownEditor id={id} value={value ?? ''} onChange={onChange} rows={6} />
      </FormField>
    );
  }

  switch (field.kind) {
    case 'boolean':
      return (
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <ToggleSwitch
            id={id}
            checked={Boolean(value)}
            onChange={onChange}
            label={
              <>
                {label}
                {field.required && (
                  <span style={{ color: 'var(--danger, #dc3545)', marginLeft: '0.25rem' }} aria-hidden="true">
                    *
                  </span>
                )}
              </>
            }
          />
          {error && (
            <span className="form-error" style={{ color: 'var(--danger, #dc3545)', fontSize: '0.85rem', display: 'block' }}>
              {error}
            </span>
          )}
        </div>
      );

    case 'textarea':
      return (
        <FormField htmlFor={id} label={label} required={field.required} error={error}>
          <textarea
            id={id}
            value={value ?? ''}
            maxLength={field.maxLength}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
          />
        </FormField>
      );

    case 'number':
    case 'decimal':
      return (
        <FormField htmlFor={id} label={label} required={field.required} error={error}>
          <input
            type="number"
            id={id}
            value={value ?? ''}
            step={field.kind === 'decimal' ? 'any' : undefined}
            style={baseInputStyle}
            onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </FormField>
      );

    case 'date':
      return (
        <FormField htmlFor={id} label={label} required={field.required} error={error}>
          <input type="date" id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
        </FormField>
      );

    case 'datetime':
      return (
        <FormField htmlFor={id} label={label} required={field.required} error={error}>
          <input
            type="datetime-local"
            id={id}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
        </FormField>
      );

    case 'enum':
      return (
        <FormField htmlFor={id} label={label} required={field.required} error={error}>
          <select id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
            <option value="">{t('common.select', 'Select...')}</option>
            {(field.enumValues || []).map((ev) => (
              <option key={ev} value={ev}>
                {/* Prefer a page-supplied existing key; else the generated fallback. */}
                {enumLabelKeys?.[ev] ? t(enumLabelKeys[ev], ev) : t(`${entity}s.enum.${ev}`, ev)}
              </option>
            ))}
          </select>
        </FormField>
      );

    case 'fk':
      return (
        <FkWidget
          field={field}
          value={value}
          onChange={onChange}
          error={error}
          label={label}
          id={id}
          optionsFetcher={optionsFetcher}
        />
      );

    case 'text':
      return (
        <FormField htmlFor={id} label={label} required={field.required} error={error}>
          <input
            type="text"
            id={id}
            value={value ?? ''}
            maxLength={field.maxLength}
            style={baseInputStyle}
            onChange={(e) => onChange(e.target.value)}
          />
        </FormField>
      );

    default:
      // json / array / unknown without an override → loud warning.
      return (
        <DevWarningBox
          message={`field "${field.name}" has kind "${field.kind}" which requires a custom override widget.`}
        />
      );
  }
};

/**
 * Widget for an m2m relation. Fetches the target option list lazily via the
 * target registry and renders a MultiSelect. Value is number[].
 */
export const RelationWidget: React.FC<{
  relation: MetaRelation;
  value: SelectId[];
  onChange: (value: number[]) => void;
  error?: string;
  /** Optional per-relation fetcher overriding the target registry. */
  optionsFetcher?: () => Promise<TargetOption[]>;
}> = ({ relation, value, onChange, error, optionsFetcher }) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<MultiSelectOption[]>([]);

  const fetcher = optionsFetcher ?? getTargetFetcher(relation.target);

  useEffect(() => {
    let cancelled = false;
    if (!fetcher) return;
    fetcher()
      .then((opts) => {
        if (!cancelled) setOptions(opts as MultiSelectOption[]);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [fetcher]);

  if (!fetcher) {
    return (
      <DevWarningBox
        message={`relation "${relation.name}" targets "${relation.target}" which is not registered in targets.ts.`}
      />
    );
  }

  return (
    <div style={{ marginBottom: '1rem' }}>
      <MultiSelect
        id={`sf-${relation.name}`}
        label={t(relation.labelKey, relation.name)}
        options={options}
        selectedIds={(value ?? []).map(String)}
        onChange={(ids) => onChange(ids.map(Number).filter(Number.isFinite))}
      />
      {error && (
        <span className="form-error" style={{ color: 'var(--danger, #dc3545)', fontSize: '0.85rem', display: 'block' }}>
          {error}
        </span>
      )}
    </div>
  );
};

/**
 * Widget for a single foreign-key (`kind: 'fk'`) scalar field. Renders a native
 * select (matching the enum widget styling) with a blank "—" option mapping to
 * null. Options come from the target registry, or a page-supplied fetcher
 * (mirrors the m2m `relationOptions` override mechanism). Value is number|null.
 */
export const FkWidget: React.FC<{
  field: SchemaFieldProps['field'];
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  error?: string;
  label: string;
  id: string;
  optionsFetcher?: () => Promise<TargetOption[]>;
}> = ({ field, value, onChange, error, label, id, optionsFetcher }) => {
  const [options, setOptions] = useState<TargetOption[]>([]);

  const fetcher = optionsFetcher ?? (field.reference ? getTargetFetcher(field.reference.target) : undefined);

  useEffect(() => {
    let cancelled = false;
    if (!fetcher) return;
    fetcher()
      .then((opts) => {
        if (!cancelled) setOptions(opts);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [fetcher]);

  if (!fetcher) {
    return (
      <DevWarningBox
        message={`fk field "${field.name}" targets "${field.reference?.target ?? '(none)'}" which is not registered in targets.ts.`}
      />
    );
  }

  return (
    <FormField htmlFor={id} label={label} required={field.required} error={error}>
      <SearchableSelect
        id={id}
        options={options}
        value={value ?? null}
        onChange={(v) => onChange(v == null ? null : Number(v))}
      />
    </FormField>
  );
};

/** Normalize a meta field to the SchemaFieldProps.field descriptor. */
export function toFieldDescriptor(f: MetaField): SchemaFieldProps['field'] {
  return {
    name: f.name,
    kind: f.kind,
    required: f.required,
    maxLength: f.maxLength,
    enumValues: f.enumValues,
    reference: f.reference,
    widget: f.widget,
    labelKey: f.labelKey,
  };
}
